import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  MapPin,
  Clock,
} from 'lucide-react';
import { OrganizationWithProgress } from '@/hooks/use-organization-pipeline';
import {
  getCurrentStage,
  formatDate,
  getProgressBarColor,
} from '@/lib/dashboard-utils';

interface OrganizationPipelineCardProps {
  organization: OrganizationWithProgress;
  onNavigate?: (orgId: string) => void;
}

export function OrganizationPipelineCard({ organization, onNavigate }: OrganizationPipelineCardProps) {
  const stage = getCurrentStage(organization.status);
  const progressColor = getProgressBarColor(organization.progressPercentage);

  const totalITTasks = organization.itTasksPending + organization.itTasksCompleted;
  const totalDataTasks = organization.dataTasksPending + organization.dataTasksCompleted;
  const totalPlatformTasks = organization.platformTasksPending + organization.platformTasksCompleted;

  return (
    <Card 
      className='hover:shadow-lg transition-shadow cursor-pointer'
      onClick={() => onNavigate?.(organization.id)}
    >
      <CardHeader className='pb-3'>
        <div className='flex items-start gap-3'>
          {/* Organization Logo/Icon */}
          {organization.logo_url ? (
            <div className='w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
              <img
                src={organization.logo_url}
                alt={`${organization.name} logo`}
                className='w-full h-full object-cover'
              />
            </div>
          ) : (
            <div className='w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0'>
              <Building2 className='w-6 h-6 text-gray-400' />
            </div>
          )}

          <div className='flex-1 min-w-0'>
            <div className='flex items-start justify-between gap-2'>
              <div>
                <CardTitle className='text-base'>{organization.name}</CardTitle>
                <div className='flex items-center gap-1 mt-1 text-sm text-gray-600 dark:text-gray-400'>
                  <MapPin className='h-4 w-4' />
                  {organization.locationCount} location{organization.locationCount !== 1 ? 's' : ''}
                </div>
              </div>
              <Badge className={stage.color}>{stage.displayName}</Badge>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Progress Bar */}
        <div className='space-y-2'>
          <div className='flex justify-between items-center'>
            <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
              Pipeline Progress
            </span>
            <span className='text-sm font-bold text-gray-900 dark:text-gray-100'>
              {organization.progressPercentage}%
            </span>
          </div>
          <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5'>
            <div
              className={`h-2.5 rounded-full transition-all bg-gradient-to-r ${progressColor}`}
              style={{ width: `${organization.progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Task Status Breakdown */}
        <div className='grid grid-cols-3 gap-2 py-3 border-y border-gray-200 dark:border-gray-700'>
          {/* IT Tasks */}
          <div className='space-y-1'>
            <div className='flex items-center justify-between text-xs text-gray-600 dark:text-gray-400'>
              <span>IT</span>
              {organization.itTasksPending > 0 && (
                <span className='text-yellow-600 dark:text-yellow-400 font-semibold'>
                  {organization.itTasksPending}
                </span>
              )}
            </div>
            <div className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
              {organization.itTasksCompleted}/{totalITTasks}
            </div>
            <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5'>
              <div
                className='bg-orange-500 h-1.5 rounded-full'
                style={{
                  width:
                    totalITTasks > 0
                      ? `${Math.round((organization.itTasksCompleted / totalITTasks) * 100)}%`
                      : '0%',
                }}
              />
            </div>
          </div>

          {/* Data Tasks */}
          <div className='space-y-1'>
            <div className='flex items-center justify-between text-xs text-gray-600 dark:text-gray-400'>
              <span>Data</span>
              {organization.dataTasksPending > 0 && (
                <span className='text-yellow-600 dark:text-yellow-400 font-semibold'>
                  {organization.dataTasksPending}
                </span>
              )}
            </div>
            <div className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
              {organization.dataTasksCompleted}/{totalDataTasks}
            </div>
            <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5'>
              <div
                className='bg-green-500 h-1.5 rounded-full'
                style={{
                  width:
                    totalDataTasks > 0
                      ? `${Math.round((organization.dataTasksCompleted / totalDataTasks) * 100)}%`
                      : '0%',
                }}
              />
            </div>
          </div>

          {/* Platform Tasks */}
          <div className='space-y-1'>
            <div className='flex items-center justify-between text-xs text-gray-600 dark:text-gray-400'>
              <span>Platform</span>
              {organization.platformTasksPending > 0 && (
                <span className='text-yellow-600 dark:text-yellow-400 font-semibold'>
                  {organization.platformTasksPending}
                </span>
              )}
            </div>
            <div className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
              {organization.platformTasksCompleted}/{totalPlatformTasks}
            </div>
            <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5'>
              <div
                className='bg-blue-500 h-1.5 rounded-full'
                style={{
                  width:
                    totalPlatformTasks > 0
                      ? `${Math.round((organization.platformTasksCompleted / totalPlatformTasks) * 100)}%`
                      : '0%',
                }}
              />
            </div>
          </div>
        </div>

        {/* Status Summary */}
        <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
          <Clock className='h-4 w-4' />
          <span>Last updated: {formatDate(organization.lastActivityAt)}</span>
        </div>
      </CardContent>
    </Card>
  );
}
